// components/Modal/DateChangeConfirmationModal.js

import React from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import Button from '../Button/Button';
import Typography from '../Typography';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '../Icons/CalendarIcon';
import WarningOutlineIcon from '../Icons/WarningOutlineIcon';
import { getIntlLocale, isSpanishLanguage } from '../../utils/dateUtils';

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const WarningContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.fill.warningAlphaMain16};
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const NewEventDateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  padding: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.radius.xs};
  gap: ${({ theme }) => theme.sizing.xs};
`;

const NewDateTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const WarningTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-bottom: ${({ theme }) => theme.sizing.xs};
`;

const DateChangeConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  attendeesCount,
  hasPaidTickets = false,
  newDate,
  newTime,
  newEndDate,
  newEndTime
}) => {
  const { t, i18n } = useTranslation('eventManagement');

  // Función para formatear la fecha en el formato solicitado
  const formatDateTime = (dateStr, timeStr, endDateStr, endTimeStr) => {
    try {
      if (!dateStr || !timeStr) return '';

      // Crear fecha usando UTC para evitar conversiones de timezone automáticas
      const date = new Date(`${dateStr}T${timeStr}:00Z`);
      
      // Usar la fecha y hora de fin reales si están disponibles, sino asumir 1 hora de duración
      let endDate;
      if (endDateStr && endTimeStr) {
        endDate = new Date(`${endDateStr}T${endTimeStr}:00Z`);
      } else {
        endDate = new Date(date.getTime() + 60 * 60 * 1000); // Asumimos 1 hora de duración por defecto
      }

      const locale = getIntlLocale(i18n.language);
      const isSpanish = isSpanishLanguage(i18n.language);

      // Verificar si el evento es en el mismo día
      const isSameDay = date.getUTCDate() === endDate.getUTCDate() && 
                       date.getUTCMonth() === endDate.getUTCMonth() && 
                       date.getUTCFullYear() === endDate.getUTCFullYear();

      // Formatear horas
      const startTime = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
      const endTime = endDate.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });

      if (isSameDay) {
        // Evento en el mismo día: Viernes, 19 de Septiembre de 2025 · 09:00 - 10:00
        const weekday = date.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
        const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
        const month = date.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
        const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();

        if (isSpanish) {
          return `${capitalizedWeekday}, ${day} de ${capitalizedMonth} de ${year} · ${startTime} - ${endTime}`;
        } else {
          return `${capitalizedWeekday}, ${capitalizedMonth} ${day}, ${year} · ${startTime} - ${endTime}`;
        }
      } else {
        // Evento en múltiples días: Viernes, 19 de Septiembre de 2025 · 09:00 - Sábado, 20 de Septiembre de 2025 · 18:00
        const startWeekday = date.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
        const capitalizedStartWeekday = startWeekday.charAt(0).toUpperCase() + startWeekday.slice(1);
        const startMonth = date.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
        const capitalizedStartMonth = startMonth.charAt(0).toUpperCase() + startMonth.slice(1);
        const startDay = date.getUTCDate();
        const startYear = date.getUTCFullYear();

        const endWeekday = endDate.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
        const capitalizedEndWeekday = endWeekday.charAt(0).toUpperCase() + endWeekday.slice(1);
        const endMonth = endDate.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
        const capitalizedEndMonth = endMonth.charAt(0).toUpperCase() + endMonth.slice(1);
        const endDay = endDate.getUTCDate();
        const endYear = endDate.getUTCFullYear();

        if (isSpanish) {
          return `${capitalizedStartWeekday}, ${startDay} de ${capitalizedStartMonth} de ${startYear} · ${startTime} - ${capitalizedEndWeekday}, ${endDay} de ${capitalizedEndMonth} de ${endYear} · ${endTime}`;
        } else {
          return `${capitalizedStartWeekday}, ${capitalizedStartMonth} ${startDay}, ${startYear} · ${startTime} - ${capitalizedEndWeekday}, ${capitalizedEndMonth} ${endDay}, ${endYear} · ${endTime}`;
        }
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return `${dateStr} ${timeStr}`;
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={t('dateChangeModal.title')}
      onClose={onClose}
      maxWidth="600px"
    >
      <ModalBody>
        <InfoSection>
          <Typography
            $variant="body-1-medium"
            $variantTablet="body-2-medium"
            $variantMobile="body-2-medium"
            as="p"
            color={theme.colors.defaultMain}
          >
            {t('dateChangeModal.description', { count: attendeesCount })}
          </Typography>

          {newDate && newTime && (
            <NewEventDateWrapper>
              <NewDateTitle>
                <CalendarIcon fill={theme.colors.defaultStrong} width="20" height="20" />
                <Typography
                  $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold"
                  as="p"
                  color={theme.colors.defaultStrong}
                >
                  {t('dateChangeModal.newDateTime')}
                </Typography>
              </NewDateTitle>

              <Typography
                $variant="body-2-medium"
                as="p"
                color={theme.colors.defaultStrong}
              >
                {formatDateTime(newDate, newTime, newEndDate, newEndTime)}
              </Typography>
            </NewEventDateWrapper>
          )}
        </InfoSection>

        {hasPaidTickets && (
          <WarningContainer>
            <WarningTitle>
              <WarningOutlineIcon width={20} height={20} fill={theme.colors.warningMain} />
              <Typography as="p" color={theme.colors.warningMain} $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold">
                {t('dateChangeModal.paidTicketsWarning.title')}
              </Typography>
            </WarningTitle>
            <Typography as="p" color={theme.colors.defaultStrong} $variant="body-2-regular">
              {t('dateChangeModal.paidTicketsWarning.description')}
            </Typography>
          </WarningContainer>
        )}
      </ModalBody>
      <ActionsContainer>
        <Button
          $variant="ghost"
          size="l"
          sizeTablet="l"
          sizeMobile="m"
          onClick={onClose}
        >
          {t('dateChangeModal.cancelButton')}
        </Button>
        <Button
          $variant="outline"
          size="l"
          sizeTablet="l"
          sizeMobile="m"
          onClick={onConfirm}
        >
          {t('dateChangeModal.confirmButton')}
        </Button>
      </ActionsContainer>
    </Modal>
  );
};

export default DateChangeConfirmationModal;
